// src/modules/goldVault/pages/VaultReconciliation.jsx
import React, { useEffect, useState } from 'react';
import {
  Card, Row, Col, Statistic, Typography, Button, Spin, Select, Alert, Tabs,
} from 'antd';
import { ReloadOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { getBranchReconciliation, getHOReconciliation } from '../api';
import { fetchBranches } from '../../branches/api';

const { Title } = Typography;
const fmtW = (v) => `${Number(v || 0).toFixed(3)}g`;

function BranchRecon() {
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBranches().then((r) => {
      const list = r.data || [];
      setBranches(list);
      if (list.length) setSelectedBranch(list[0]._id);
    }).catch(() => {});
  }, []);

  useEffect(() => { if (selectedBranch) load(); }, [selectedBranch]);

  const load = async () => {
    if (!selectedBranch) return;
    setLoading(true);
    try {
      const res = await getBranchReconciliation(selectedBranch);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const matched = data && Math.abs(data.grossDifference || 0) < 0.001 && Math.abs(data.netDifference || 0) < 0.001;

  return (
    <div>
      <Row gutter={12} align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Select placeholder="Select Branch" style={{ width: 200 }} value={selectedBranch} onChange={setSelectedBranch}>
            {branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>)}
          </Select>
        </Col>
        <Col><Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button></Col>
      </Row>
      <Spin spinning={loading}>
        {data && (
          <>
            <Alert
              type={matched ? 'success' : 'warning'}
              icon={matched ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
              message={matched ? 'Vault and Ledger are balanced' : 'Variance detected — investigate'}
              style={{ marginBottom: 16 }}
            />
            <Row gutter={[16, 16]}>
              {[
                { title: 'Vault Gross (snapshot)', value: fmtW(data.vault?.gross) },
                { title: 'Ledger Gross (running)', value: fmtW(data.ledger?.gross) },
                { title: 'Gross Difference', value: fmtW(data.grossDifference), valueStyle: { color: data.grossDifference !== 0 ? '#cf1322' : '#3f8600' } },
                { title: 'Net Difference', value: fmtW(data.netDifference), valueStyle: { color: data.netDifference !== 0 ? '#cf1322' : '#3f8600' } },
              ].map((s) => (
                <Col xs={12} sm={6} key={s.title}>
                  <Card size="small" bordered>
                    <Statistic title={s.title} value={s.value} valueStyle={s.valueStyle || { fontSize: 18 }} />
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}
      </Spin>
    </div>
  );
}

function HORecon() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getHOReconciliation();
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const matched = data && Math.abs(data.grossDifference || 0) < 0.001;

  return (
    <div>
      <Row style={{ marginBottom: 12 }}>
        <Col><Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button></Col>
      </Row>
      <Spin spinning={loading}>
        {data && (
          <>
            <Alert
              type={matched ? 'success' : 'warning'}
              message={matched ? 'HO Vault balanced' : 'HO Vault variance detected'}
              style={{ marginBottom: 16 }}
            />
            <Row gutter={[16, 16]}>
              {[
                { title: 'Vault Gross', value: fmtW(data.vault?.gross) },
                { title: 'Ledger Gross', value: fmtW(data.ledger?.gross) },
                { title: 'Gross Difference', value: fmtW(data.grossDifference), valueStyle: { color: data.grossDifference !== 0 ? '#cf1322' : '#3f8600' } },
              ].map((s) => (
                <Col xs={12} sm={6} key={s.title}>
                  <Card size="small" bordered>
                    <Statistic title={s.title} value={s.value} valueStyle={s.valueStyle || { fontSize: 18 }} />
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}
      </Spin>
    </div>
  );
}

export default function VaultReconciliation() {
  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 20 }}>Vault Reconciliation</Title>
      <Tabs
        items={[
          { key: 'branch', label: 'Branch Vault', children: <BranchRecon /> },
          { key: 'ho', label: 'HO Vault', children: <HORecon /> },
        ]}
      />
    </div>
  );
}
